<template>
  <v-content class="gb">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="12">
          <v-card
            :loading="loading"
            class="fill-width table_theme card-theme custom-container"
          >
            <v-card-title>
              <span class="title_table medium-font" style="letter-spacing: normal">
                Coverage
              </span>
              <v-spacer></v-spacer>
              <v-btn rounded small dark color="#061D47"
                  style="width: 120px !important; margin-right: 8px; text-transform: none; font-weight: 600;"
                  @click="$router.push('/owner/vendors')"
              >Vendors</v-btn>
              <v-btn rounded small dark color="#5C6BC0"
                  style="width: 120px !important; margin-right: 8px; text-transform: none; font-weight: 600;"
                  @click="$router.push('/owner/vendor-recommendations')"
              >Referrals</v-btn>
              <div v-if="coverage" class="d-flex align-center" style="gap: 12px;">
                <span
                  class="legend-item"
                  :class="{ 'legend-item--active': statusFilterCoverage === null }"
                  @click="statusFilterCoverage = null"
                >
                  All <strong>{{ sortedCategories.length }}</strong>
                </span>
                <span style="color: #ddd;">|</span>
                <span
                  class="legend-item"
                  :class="{ 'legend-item--active': statusFilterCoverage === 'gap' }"
                  @click="statusFilterCoverage = statusFilterCoverage === 'gap' ? null : 'gap'"
                >
                  <v-icon x-small color="#F44336" class="mr-1">brightness_1</v-icon>
                  Gap <strong>{{ statusCount('gap') }}</strong>
                </span>
                <span
                  class="legend-item"
                  :class="{ 'legend-item--active': statusFilterCoverage === 'pending' }"
                  @click="statusFilterCoverage = statusFilterCoverage === 'pending' ? null : 'pending'"
                >
                  <v-icon x-small color="#9E9E9E" class="mr-1">brightness_1</v-icon>
                  Invited <strong>{{ statusCount('pending') }}</strong>
                </span>
                <span
                  class="legend-item"
                  :class="{ 'legend-item--active': statusFilterCoverage === 'single' }"
                  @click="statusFilterCoverage = statusFilterCoverage === 'single' ? null : 'single'"
                >
                  <v-icon x-small color="#FF9800" class="mr-1">brightness_1</v-icon>
                  Single <strong>{{ statusCount('single') }}</strong>
                </span>
                <span
                  class="legend-item"
                  :class="{ 'legend-item--active': statusFilterCoverage === 'covered' }"
                  @click="statusFilterCoverage = statusFilterCoverage === 'covered' ? null : 'covered'"
                >
                  <v-icon x-small color="#4CAF50" class="mr-1">brightness_1</v-icon>
                  Covered <strong>{{ statusCount('covered') }}</strong>
                </span>
              </div>
            </v-card-title>
            <v-divider style="max-width: 1096px;"></v-divider>

            <!-- Service area -->
            <div class="px-4 pt-3 d-flex align-center" style="gap: 8px;">
              <v-icon small color="#061D47">location_on</v-icon>
              <span style="font-size: 13px; font-weight: 500;">Service area:</span>
              <template v-if="serviceArea">
                <span style="font-size: 13px;">{{ serviceArea }}</span>
                <v-btn x-small text color="#29ABE2" @click="showServiceAreaInput = true">Change</v-btn>
              </template>
              <template v-else>
                <GmapAutocomplete
                  placeholder="Enter your city"
                  @place_changed="onPlaceChanged"
                  :options="{ types: ['(cities)'], componentRestrictions: { country: ['us', 'ca'] } }"
                  class="service-area-autocomplete"
                ></GmapAutocomplete>
              </template>
              <template v-if="showServiceAreaInput && serviceArea">
                <GmapAutocomplete
                  placeholder="Enter new city"
                  @place_changed="onPlaceChanged"
                  :options="{ types: ['(cities)'], componentRestrictions: { country: ['us', 'ca'] } }"
                  class="service-area-autocomplete"
                ></GmapAutocomplete>
                <v-btn x-small text @click="showServiceAreaInput = false">Cancel</v-btn>
              </template>
            </div>

            <v-card-text v-if="coverage">
              <!-- Common categories grid -->
              <div class="d-flex align-center mb-3" style="gap: 8px;">
                <h4 style="color: #333; font-size: 14px;">Vendor Categories</h4>
                <v-spacer></v-spacer>
                <v-text-field
                  v-model="searchQuery"
                  placeholder="Search categories..."
                  prepend-inner-icon="search"
                  dense
                  hide-details
                  solo
                  flat
                  clearable
                  background-color="#f5f5f5"
                  style="max-width: 200px;"
                  class="category-search-input"
                ></v-text-field>
                <v-btn small text color="#29ABE2" @click="showAddCategory = !showAddCategory">
                  <v-icon small left>add</v-icon> Add Category
                </v-btn>
              </div>
              <div v-if="showAddCategory" class="d-flex align-center mb-3" style="gap: 6px;">
                <v-autocomplete
                  v-model="newCategory"
                  :items="availableCategories"
                  placeholder="Search categories..."
                  dense
                  hide-details
                  outlined
                  style="max-width: 200px; font-size: 11px;"
                  :menu-props="{ maxHeight: 200 }"
                  class="add-category-input"
                ></v-autocomplete>
                <v-btn x-small color="#29ABE2" dark @click="addCategory" :disabled="!newCategory">Add</v-btn>
                <v-btn x-small text @click="showAddCategory = false; newCategory = ''">Cancel</v-btn>
              </div>
              <div class="coverage-grid">
                <div
                  v-for="cat in filteredCategories"
                  :key="cat.category"
                  class="coverage-item"
                  :class="{
                    'coverage-item--gap': cat.status === 'gap',
                    'coverage-item--pending': cat.status === 'pending',
                    'coverage-item--single': cat.status === 'single',
                    'coverage-item--covered': cat.status === 'covered',
                    'coverage-item--clickable': cat.status === 'gap' || cat.status === 'pending',
                  }"
                  @click="(cat.status === 'gap' || cat.status === 'pending') ? searchVendors(cat.category) : null"
                >
                  <div class="coverage-item__header">
                    <v-icon
                      small
                      :color="statusColor(cat.status)"
                      class="mr-1"
                    >brightness_1</v-icon>
                    <span class="coverage-item__name">{{ cat.category }}</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="isCustomCategory(cat.category)" icon x-small @click.stop="removeCategory(cat.category)" style="margin-right: 4px;">
                      <v-icon x-small color="#999">close</v-icon>
                    </v-btn>
                    <span class="coverage-item__count">{{ cat.vendor_count }}</span>
                  </div>
                  <div v-if="cat.vendors.length > 0" class="coverage-item__vendors">
                    <div v-for="v in cat.vendors" :key="v.id" class="coverage-item__vendor">
                      {{ v.company_name || v.name }}
                      <span v-if="v.status && v.status !== 'Active'" class="coverage-item__vendor-status" :class="'coverage-item__vendor-status--' + v.status.toLowerCase()">
                        {{ v.status }}
                      </span>
                    </div>
                  </div>
                  <div v-else class="coverage-item__empty">
                    <v-icon x-small color="#F44336" class="mr-1">search</v-icon>
                    Click to find vendors
                  </div>
                </div>
              </div>

            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Vendor search results dialog -->
      <v-dialog v-model="searchDialog" max-width="700" scrollable>
        <v-card>
          <v-card-title class="d-flex align-center">
            <span style="text-transform: capitalize;">{{ searchCategory }} vendors near {{ serviceArea }}</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="searchDialog = false"><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-divider></v-divider>

          <div style="padding: 8px 16px; background: #FFF8E1; font-size: 11px; color: #795548;">
            <v-icon x-small color="#795548" class="mr-1">info</v-icon>
            These results are from public listings. Kiey does not endorse or vet these vendors. Review and contact them before adding to your network.
          </div>

          <v-card-text style="max-height: 500px;">
            <div v-if="searchLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="#29ABE2"></v-progress-circular>
              <p class="mt-2" style="color: #888; font-size: 13px;">Searching for vendors...</p>
            </div>

            <div v-else-if="searchResults.length === 0" class="text-center py-8">
              <p style="color: #888; font-size: 14px;">No results found for "{{ searchCategory }}" near {{ serviceArea }}.</p>
            </div>

            <div v-else>
              <v-card
                v-for="(place, index) in searchResults"
                :key="index"
                outlined
                class="mb-3"
                style="border-radius: 8px;"
              >
                <v-card-text class="py-3">
                  <div class="d-flex align-center">
                    <div style="flex: 1;">
                      <div style="font-weight: 600; font-size: 14px; color: #333;">{{ place.name }}</div>
                      <div style="font-size: 12px; color: #888;" v-if="place.vicinity || place.formatted_address">
                        <v-icon x-small color="#888" class="mr-1">location_on</v-icon>
                        <a :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(place.name + ' ' + (place.vicinity || place.formatted_address))" target="_blank" style="color: #888; text-decoration: none;">
                          {{ place.vicinity || place.formatted_address }}
                        </a>
                      </div>
                      <div class="d-flex align-center mt-1" style="gap: 12px;">
                        <span v-if="place.rating" style="font-size: 12px; color: #FF9800;">
                          <v-icon x-small color="#FF9800">star</v-icon>
                          {{ place.rating }} <span style="color: #aaa;">({{ place.user_ratings_total || 0 }})</span>
                        </span>
                        <span v-if="place.opening_hours" style="font-size: 11px;" :style="{ color: place.opening_hours.open_now ? '#4CAF50' : '#F44336' }">
                          {{ place.opening_hours.open_now ? 'Open now' : 'Closed' }}
                        </span>
                      </div>
                    </div>
                    <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 4px;">
                      <div class="d-flex" style="gap: 8px;">
                        <v-btn v-if="place.phone" small text color="#4CAF50" :href="'tel:' + place.phone" style="text-transform: none; min-width: 0; padding: 0 8px;">
                          <v-icon small left>phone</v-icon> {{ place.phone }}
                        </v-btn>
                        <v-btn v-if="place.website" small text color="#1565C0" :href="place.website" target="_blank" style="text-transform: none; min-width: 0; padding: 0 8px;">
                          <v-icon small left>language</v-icon> Website
                        </v-btn>
                        <v-btn small outlined color="#29ABE2" :href="'https://www.google.com/maps/place/?q=place_id:' + place.place_id" target="_blank">
                          <v-icon small left>open_in_new</v-icon> View
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- No service area dialog -->
      <v-dialog v-model="noServiceAreaDialog" max-width="400">
        <v-card>
          <v-card-title>Set Your Service Area</v-card-title>
          <v-card-text>
            <p style="font-size: 13px; color: #666;">Enter your city to search for vendors in your area.</p>
            <GmapAutocomplete
              placeholder="e.g. Champaign, IL"
              @place_changed="onDialogPlaceChanged"
              :options="{ types: ['(cities)'], componentRestrictions: { country: ['us', 'ca'] } }"
              class="service-area-autocomplete service-area-autocomplete--dialog"
            ></GmapAutocomplete>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="noServiceAreaDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>
  </v-content>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "VendorCoverage",
  data() {
    return {
      serviceArea: localStorage.getItem('kiey_service_area') || '',
      serviceAreaInput: '',
      showServiceAreaInput: false,
      showAddCategory: false,
      statusFilterCoverage: null,
      searchQuery: '',
      newCategory: '',
      customCategories: JSON.parse(localStorage.getItem('kiey_custom_categories') || '[]'),
      searchDialog: false,
      searchCategory: '',
      searchResults: [],
      searchLoading: false,
      noServiceAreaDialog: false,
      pendingSearchCategory: '',
    };
  },
  computed: {
    ...mapState({
      coverage: state => state.Referral_engine.coverage,
      status: state => state.Referral_engine.status,
      allServices: state => state.Services.services,
    }),
    loading() {
      return this.status === 'loading';
    },
    sortedCategories() {
      if (!this.coverage) return [];
      const all = [
        ...this.coverage.common_categories,
        ...this.coverage.additional_categories,
      ];
      // Add custom categories as gaps if not already present
      const existingNames = new Set(all.map(c => c.category));
      for (const cat of this.customCategories) {
        if (!existingNames.has(cat)) {
          all.push({ category: cat, status: 'gap', vendor_count: 0, vendors: [] });
        }
      }
      const order = { gap: 0, pending: 1, single: 2, covered: 3 };
      return all.sort((a, b) => (order[a.status] ?? 2) - (order[b.status] ?? 2));
    },
    filteredCategories() {
      let cats = this.sortedCategories;
      if (this.statusFilterCoverage) {
        cats = cats.filter(c => c.status === this.statusFilterCoverage);
      }
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        cats = cats.filter(c => c.category.toLowerCase().includes(q));
      }
      return cats;
    },
    availableCategories() {
      if (!this.allServices || this.allServices.length === 0) return [];
      const shown = new Set(this.sortedCategories.map(c => c.category));
      const all = this.allServices
        .map(s => (s.value || s.text || '').toLowerCase().trim())
        .filter(s => s && !shown.has(s));
      // Deduplicate near-matches: if "locksmith" and "locksmith services" both exist, keep the shorter one
      const deduped = all.filter(name => {
        return !all.some(other => other !== name && other.length < name.length && name.includes(other));
      });
      return deduped.sort();
    },
  },
  created() {
    this.$store.dispatch("Banner/show");
    this.$store.dispatch('Referral_engine/fetchCoverage');
    this.$store.dispatch('Services/get_services');
  },
  methods: {
    statusColor(status) {
      switch (status) {
        case 'gap': return '#F44336';
        case 'pending': return '#9E9E9E';
        case 'single': return '#FF9800';
        case 'covered': return '#4CAF50';
        default: return '#E0E0E0';
      }
    },
    statusCount(status) {
      return this.sortedCategories.filter(c => c.status === status).length;
    },
    addCategory() {
      const cat = (this.newCategory || '').toLowerCase().trim();
      if (cat && !this.customCategories.includes(cat)) {
        this.customCategories.push(cat);
        localStorage.setItem('kiey_custom_categories', JSON.stringify(this.customCategories));
      }
      this.newCategory = '';
      this.showAddCategory = false;
    },
    removeCategory(category) {
      this.customCategories = this.customCategories.filter(c => c !== category);
      localStorage.setItem('kiey_custom_categories', JSON.stringify(this.customCategories));
    },
    isCustomCategory(category) {
      return this.customCategories.includes(category);
    },
    onPlaceChanged(place) {
      if (place && place.formatted_address) {
        this.serviceArea = place.formatted_address;
        localStorage.setItem('kiey_service_area', this.serviceArea);
        this.showServiceAreaInput = false;
        if (this.pendingSearchCategory) {
          this.searchVendors(this.pendingSearchCategory);
          this.pendingSearchCategory = '';
        }
      }
    },
    onDialogPlaceChanged(place) {
      if (place && place.formatted_address) {
        this.serviceArea = place.formatted_address;
        localStorage.setItem('kiey_service_area', this.serviceArea);
        this.noServiceAreaDialog = false;
        if (this.pendingSearchCategory) {
          this.searchVendors(this.pendingSearchCategory);
          this.pendingSearchCategory = '';
        }
      }
    },
    searchVendors(category) {
      if (!this.serviceArea) {
        this.pendingSearchCategory = category;
        this.serviceAreaInput = '';
        this.noServiceAreaDialog = true;
        return;
      }

      this.searchCategory = category;
      this.searchResults = [];
      this.searchLoading = true;
      this.searchDialog = true;

      // Wait for Google Maps to be ready
      this.$gmapApiPromiseLazy().then(() => {
        const google = window.google;
        if (!google || !google.maps || !google.maps.places) {
          console.error('Google Maps Places API not available');
          this.searchLoading = false;
          return;
        }

        const service = new google.maps.places.PlacesService(
          document.createElement('div')
        );

        service.textSearch(
          {
            query: `${category} near ${this.serviceArea}`,
            type: 'establishment',
          },
          (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results) {
              const places = results.slice(0, 15);
              this.searchResults = places;
              // Fetch details (phone, website) for each result
              places.forEach((place, index) => {
                service.getDetails(
                  {
                    placeId: place.place_id,
                    fields: ['formatted_phone_number', 'website'],
                  },
                  (details, detailStatus) => {
                    if (detailStatus === google.maps.places.PlacesServiceStatus.OK && details) {
                      this.$set(this.searchResults, index, {
                        ...this.searchResults[index],
                        phone: details.formatted_phone_number || null,
                        website: details.website || null,
                      });
                    }
                  }
                );
              });
              this.searchLoading = false;
            } else {
              this.searchResults = [];
              this.searchLoading = false;
            }
          }
        );
      }).catch(err => {
        console.error('Google Maps load error:', err);
        this.searchLoading = false;
        this.searchResults = [];
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.legend-item {
  font-size: 12px;
  color: #888;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 4px;
  transition: all 0.15s;
  user-select: none;

  &:hover {
    background: #f0f0f0;
  }

  &--active {
    background: #E8EAF6;
    color: #333;
  }
}

.coverage-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.coverage-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &--gap {
    border-color: #FFCDD2;
    background: #FFF5F5;
  }

  &--single {
    border-color: #FFE0B2;
    background: #FFFAF0;
  }

  &--covered {
    border-color: #C8E6C9;
    background: #F5FFF5;
  }

  &--pending {
    border-color: #E0E0E0;
    background: #FAFAFA;
  }

  &--clickable {
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 12px rgba(244, 67, 54, 0.15);
      border-color: #EF9A9A;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    text-transform: capitalize;
    color: #333;
  }

  &__count {
    font-size: 12px;
    font-weight: 600;
    color: #888;
  }

  &__vendors {
    padding-left: 20px;
  }

  &__vendor {
    font-size: 11px;
    color: #666;
    line-height: 1.6;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__vendor-status {
    font-size: 9px;
    padding: 1px 5px;
    border-radius: 3px;
    font-weight: 600;

    &--invited {
      background: #E3F2FD;
      color: #1565C0;
    }

    &--onboarding {
      background: #FFF3E0;
      color: #E65100;
    }

    &--cancelled {
      background: #FFEBEE;
      color: #C62828;
    }
  }

  &__empty {
    font-size: 11px;
    color: #F44336;
    padding-left: 20px;
    font-style: italic;
  }
}

.category-search-input {
  &::v-deep .v-input__slot {
    min-height: 28px !important;
    padding: 0 6px !important;
    border-radius: 6px !important;
  }
  &::v-deep input {
    font-size: 11px !important;
    padding: 0 !important;
  }
  &::v-deep .v-input__prepend-inner {
    margin-top: 3px !important;
    margin-right: 2px !important;
  }
  &::v-deep .v-input__prepend-inner .v-icon {
    font-size: 14px !important;
    color: #aaa !important;
  }
  &::v-deep .v-input__append-inner {
    margin-top: 2px !important;
  }
}

.add-category-input {
  &::v-deep .v-input__slot {
    min-height: 28px !important;
    padding: 0 8px !important;
  }
  &::v-deep input {
    font-size: 11px !important;
  }
  &::v-deep .v-input__append-inner {
    margin-top: 2px !important;
  }
}

.service-area-autocomplete {
  font-size: 13px;
  border: none;
  border-bottom: 1px solid #ccc;
  outline: none;
  padding: 4px 0;
  max-width: 280px;
  width: 280px;

  &--dialog {
    width: 100%;
    max-width: 100%;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 8px 12px;
  }
}
</style>
